import { AssessmentScore } from '@/gen/assessment/v1/assessment_score_pb'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { AIPosture } from '@/gen/inventory/v1/ai_posture_pb'
import { AlertStatus } from '@/gen/inventory/v1/alert_pb'
import { CompanyLifecycle } from '@/gen/inventory/v1/company_lifecycle_pb'
import { DataTypeSource, InherentRiskSource } from '@/gen/inventory/v1/company_service_pb'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'
import { SmartQuestionStatus } from '@/gen/inventory/v1/control_service_pb'
import { IntegrationStatus } from '@/gen/inventory/v1/integration_service_pb'
import { OngoingAssessmentStatus } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'
import { ManualQuestionStatus } from '@/gen/questionnaire/v2/model_pb'

import { DocumentStatus } from '@/const/artifact-status.types'

import { ControlImpact } from '@/lib/risk/control-impact'

/**
 * This file contains the priorities for sorting.
 * The order is from the lowest to the highest number.
 * "1" Should display first.
 * "2" Should display second.
 * ...
 * "n" Should display last.
 */

export const smartQuestionStatusPriority: Record<SmartQuestionStatus, number> = {
  [SmartQuestionStatus.UNSPECIFIED]: 0,
  [SmartQuestionStatus.UNPUBLISHED]: 1,
  [SmartQuestionStatus.PUBLISHED]: 2,
  [SmartQuestionStatus.AWAITING_REVIEW]: 3,
  [SmartQuestionStatus.AWAITING_REOPEN]: 4,
  [SmartQuestionStatus.AWAITING_RECIPIENT]: 5,
  [SmartQuestionStatus.RESOLVED]: 6,
}

export const integrationStatusPriority: Record<IntegrationStatus, number> = {
  [IntegrationStatus.UNSPECIFIED]: 0,
  [IntegrationStatus.AVAILABLE]: 1,
  [IntegrationStatus.ADDED]: 2,
  [IntegrationStatus.BETA]: 3,
  [IntegrationStatus.UNAVAILABLE]: 4,
}

export const riskLevelPriority: Record<RiskLevel, number> = {
  [RiskLevel.CRITICAL]: 1,
  [RiskLevel.HIGH]: 2,
  [RiskLevel.MEDIUM]: 3,
  [RiskLevel.LOW]: 4,
  [RiskLevel.UNSPECIFIED]: 5,
}

export const companyStatusPriority: Record<CompanyStatus, number> = {
  [CompanyStatus.UNSPECIFIED]: 0,
  [CompanyStatus.IN_ASSESSMENT]: 1,
  [CompanyStatus.ASSESSMENT_REQUIRED]: 2,
  [CompanyStatus.APPROVED]: 3,
  [CompanyStatus.REJECTED]: 4,
}

export const companyLifecyclePriority: Record<CompanyLifecycle, number> = {
  [CompanyLifecycle.UNSPECIFIED]: 0,
  [CompanyLifecycle.IN_EVALUATION]: 1,
  [CompanyLifecycle.ONBOARDED]: 2,
  [CompanyLifecycle.ARCHIVED]: 3,
  [CompanyLifecycle.UNSANCTIONED]: 4,
  [CompanyLifecycle.OFFBOARDED]: 5,
}

export const aiPosturePriority: Record<AIPosture, number> = {
  [AIPosture.AI_POSTURE_MODEL_PROVIDER]: 1,
  [AIPosture.AI_POSTURE_AI_NATIVE]: 2,
  [AIPosture.AI_POSTURE_EMBEDDED_AI]: 3,
  // NON_AI and UNSPECIFIED render as the same "AI not detected" cell, so they must sort as one group.
  [AIPosture.AI_POSTURE_NON_AI]: 4,
  [AIPosture.AI_POSTURE_UNSPECIFIED]: 4,
}

export const dataTypeSourcePriority: Record<DataTypeSource, number> = {
  [DataTypeSource.UNSPECIFIED]: 0,
  [DataTypeSource.UNDOCUMENTED_ACCESS]: 1,
  [DataTypeSource.DOCUMENTED_AND_ACCESSED]: 2,
  [DataTypeSource.DOCUMENTED_ONLY]: 3,
}

export const ongoingAssessmentStatusPriority: Record<OngoingAssessmentStatus, number> = {
  [OngoingAssessmentStatus.AWAITING_APPROVAL]: 0,
  [OngoingAssessmentStatus.IN_PROGRESS]: 1,
  [OngoingAssessmentStatus.READY_TO_START]: 2,
  [OngoingAssessmentStatus.COMPLETED]: 3,
  [OngoingAssessmentStatus.UNSPECIFIED]: 4,
}

export const alertStatusPriority: Record<AlertStatus, number> = {
  [AlertStatus.UNSPECIFIED]: 0,
  [AlertStatus.OPEN]: 1,
  [AlertStatus.ONGOING]: 2,
  [AlertStatus.RESOLVED]: 3,
  [AlertStatus.ACCEPTED]: 4,
  [AlertStatus.NOT_APPLICABLE]: 5,
}

export const controlStatusPriority: Record<ControlStatus, number> = {
  [ControlStatus.UNSPECIFIED]: 0,
  [ControlStatus.NOT_EVALUATED]: 1,
  [ControlStatus.GAP]: 2,
  [ControlStatus.NO_EVIDENCE]: 3,
  [ControlStatus.VALIDATED]: 4,
  [ControlStatus.ATTESTED]: 5,
  [ControlStatus.NOT_APPLICABLE]: 6,
}

export const documentStatusPriority: Record<DocumentStatus, number> = {
  [DocumentStatus.UNSPECIFIED]: 0,
  [DocumentStatus.COMPLETE]: 1,
  [DocumentStatus.CLASSIFYING]: 2,
  [DocumentStatus.PENDING]: 3,
}

export const questionStatusPriority: Record<ManualQuestionStatus, number> = {
  [ManualQuestionStatus.UNSPECIFIED]: 0,
  [ManualQuestionStatus.ACCEPTED]: 1,
  [ManualQuestionStatus.NEEDS_REVIEW]: 2,
  [ManualQuestionStatus.REJECTED]: 3,
  [ManualQuestionStatus.UNANSWERED]: 4,
}

export const assessmentScorePriority: Record<AssessmentScore, number> = {
  [AssessmentScore.UNACCEPTABLE]: 1,
  [AssessmentScore.CONCERNING]: 2,
  [AssessmentScore.ACCEPTABLE]: 3,
  [AssessmentScore.NOT_ASSESSED]: 4,
  [AssessmentScore.UNSPECIFIED]: 5,
}

export const controlImpactPriority: Record<ControlImpact, number> = {
  [ControlImpact.CRITICAL]: 1,
  [ControlImpact.MEDIUM]: 2,
  [ControlImpact.LOW]: 3,
}

const PROCUREMENT_INTEGRATIONS = [
  InherentRiskSource.VENDR,
  InherentRiskSource.ASANA,
  InherentRiskSource.RAMP,
  InherentRiskSource.ONETRUST,
  InherentRiskSource.GRAPHITECONNECT,
  InherentRiskSource.LOGICMANAGER,
  InherentRiskSource.ZIP,
  InherentRiskSource.WEBHOOK,
] as const

const IDP_INTEGRATIONS = [
  InherentRiskSource.MICROSOFT_ENTRA_ID,
  InherentRiskSource.GOOGLE_WORKSPACE,
  InherentRiskSource.OKTA,
] as const

const OTHER_INTEGRATIONS = [
  InherentRiskSource.INTEGRATION,
  InherentRiskSource.WIZ,
  InherentRiskSource.NETSKOPE,
] as const

export const inherentRiskSourcePriority: Record<InherentRiskSource, number> = {
  [InherentRiskSource.IRQ]: 0,
  [InherentRiskSource.INTAKE]: 1,
  ...Object.fromEntries(PROCUREMENT_INTEGRATIONS.map((s) => [s, 2])),
  ...Object.fromEntries(IDP_INTEGRATIONS.map((s) => [s, 3])),
  ...Object.fromEntries(OTHER_INTEGRATIONS.map((s) => [s, 4])),
  [InherentRiskSource.PROJECTED]: 5,
  [InherentRiskSource.UNSPECIFIED]: 6,
} as Record<InherentRiskSource, number>

export const getSortByPriority =
  <T extends number | string>(priority: Record<T, number>) =>
  (a: T, b: T) => {
    const priorityA = priority[a]
    const priorityB = priority[b]
    if (priorityA === priorityB) return 0
    if (priorityA === null) return -1
    if (priorityB === null) return 1
    return priorityA - priorityB
  }

export const sortDataTypeSource = getSortByPriority(dataTypeSourcePriority)
export const sortRiskLevel = getSortByPriority(riskLevelPriority)
export const sortControlStatus = getSortByPriority(controlStatusPriority)
export const sortQuestionStatus = getSortByPriority(questionStatusPriority)
export const sortAssessmentScore = getSortByPriority(assessmentScorePriority)
export const sortInherentRiskSource = getSortByPriority(inherentRiskSourcePriority)
export const sortAIPosture = getSortByPriority(aiPosturePriority)
